import React from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import { EditIcon, PlusIcon } from "@icons";
import type { CounterpartyComboboxValue } from "./CounterpartyCombobox";

/** A counterparty row's label and the short hint shown right after it. */
export interface CounterpartyOptionText {
    label: string;
    labelHint?: string;
}

interface Props {
    counterparties: CounterpartyComboboxValue[];
    isLoading: boolean;
    hasSelection: boolean;
    typedSearch?: string;
    searchPlaceholder: string;
    emptyStateText: string;
    clearSelectionText: string;
    createButtonText: string;
    editButtonText: string;
    hideFooter?: boolean;
    toCounterpartyOption?: (
        counterparty: CounterpartyComboboxValue,
    ) => CounterpartyOptionText;
    onClear: () => void;
    onClose: () => void;
    onCreateNew: (name: string) => void;
    onEditCounterparty?: () => void;
}

/**
 * `CounterpartyCombobox`'s popup: search, the clear row, the counterparty
 * rows with paging, and the create / edit footer.
 */
export const CounterpartyComboboxPopup: React.FC<Props> = ({
    counterparties,
    isLoading,
    hasSelection,
    typedSearch,
    searchPlaceholder,
    emptyStateText,
    clearSelectionText,
    createButtonText,
    editButtonText,
    hideFooter,
    toCounterpartyOption,
    onClear,
    onClose,
    onCreateNew,
    onEditCounterparty,
}) => (
    <Picker.Popup sideOffset={4}>
        <Picker.Search placeholder={searchPlaceholder} />
        <Picker.List>
            {hasSelection && (
                <Picker.Item onSelect={onClear}>
                    {clearSelectionText}
                </Picker.Item>
            )}
            {isLoading && <Picker.Loading>Loading...</Picker.Loading>}
            {!isLoading &&
                counterparties.map((counterparty) => {
                    const option = toCounterpartyOption?.(counterparty) ?? {
                        label: counterparty.name,
                    };
                    return (
                        <Picker.Item
                            key={counterparty.id}
                            value={counterparty.id}
                            labelHint={option.labelHint}
                        >
                            {option.label}
                        </Picker.Item>
                    );
                })}
            {!isLoading && counterparties.length === 0 && (
                <Picker.Empty>{emptyStateText}</Picker.Empty>
            )}
            <Picker.LoadMore />
            {!hideFooter && (
                <Picker.Footer>
                    <Picker.Item
                        leading={<PlusIcon />}
                        onSelect={() => onCreateNew(typedSearch ?? "")}
                    >
                        {typedSearch
                            ? `Create "${typedSearch}"`
                            : createButtonText}
                    </Picker.Item>
                    {onEditCounterparty && hasSelection ? (
                        <Picker.Item
                            leading={<EditIcon />}
                            onSelect={() => {
                                onClose();
                                onEditCounterparty();
                            }}
                        >
                            {editButtonText}
                        </Picker.Item>
                    ) : null}
                </Picker.Footer>
            )}
        </Picker.List>
    </Picker.Popup>
);
